import { Component, inject, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';

import { sentenceDTO, tokenDTO } from '@capiche/ts-data-models-shared';

import { CapicheMaterialModule } from 'capiche-material';
import { CapicheLoggerService } from 'capiche-logger';
import { DisclosurePathService, DisclosurePath } from 'capiche-disclosure-path';
import { ReplacementService, CensorPipe } from 'capiche-text-processor';

import { WordComponent } from '../word/word.component';
import { DisclosureControlsComponent } from '../disclosure-controls/disclosure-controls.component';
import { ComponentInstanceTracker } from '../../utils/component-instance-tracker';

@Component({
  selector: 'app-sentence',
  standalone: true,
  imports: [CommonModule, CapicheMaterialModule, DisclosureControlsComponent, WordComponent, CensorPipe],
  templateUrl: './sentence.component.html',
  styleUrl: './sentence.component.css',
})
export class SentenceComponent implements OnInit, OnChanges, OnDestroy {
  private disclosurePathService = inject(DisclosurePathService);
  private replacementService = inject(ReplacementService);
  private logger = new CapicheLoggerService('sentence.component:', false);

  @Input({ required: true }) sentence!: sentenceDTO;
  @Input({ required: true }) thisItemPath!: DisclosurePath;
  /** Editor instance identifier — passed through to ReplacementService. */
  @Input({ required: true }) elId!: string;
  @Input() panelSections: 'all' | 'headerOnly' | 'contentOnly' = 'all';
  /** Cursor is inside this sentence in the editor — teal border + scroll. */
  @Input() isActive: boolean = false;
  /** Editor mouse is resting over this sentence (~550ms) — teal border + scroll. */
  @Input() isHoverTarget: boolean = false;

  @ViewChild('sentenceRoot', { read: ElementRef }) sentenceRoot?: ElementRef<HTMLElement>;

  childItemPaths: Record<number, DisclosurePath> = {};

  /** True while the mouse is directly over this sidebar card. */
  isDirectHover: boolean = false;

  get panelOpenState(): boolean {
    return this.disclosurePathService.isCurrent(this.thisItemPath);
  }

  /** True when this sentence should show the full teal attention border —
   *  the cursor is here, or its panel is open. The "primary" indicator;
   *  never more than one sentence has this at once. */
  get showAttentionBorder(): boolean {
    return this.isActive || this.panelOpenState;
  }

  /** True when this sentence should show the lighter hover cue. Suppressed
   *  whenever showAttentionBorder is already true, so a sentence that's both
   *  cursor-active AND hovered only ever shows one treatment, not both —
   *  and so hovering a *different* sentence than the active one is visually
   *  distinct from "this is the active sentence" rather than looking
   *  identical (CSW-1586 follow-up: previously both used the same border,
   *  so two different rows could show what looked like the same "active"
   *  state at once). */
  get showHoverCue(): boolean {
    return (this.isHoverTarget || this.isDirectHover) && !this.showAttentionBorder;
  }

  constructor() {
    this.logger.log('constructor');
    ComponentInstanceTracker.increment('SentenceComponent');
  }

  ngOnInit() {
    this.logger.log('ngOnInit', this.sentence);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['sentence']) {
      const tokens = this.sentence.tokens;
      this.childItemPaths = tokens.reduce<Record<number, DisclosurePath>>((acc, t) => {
        acc[t.index] = this.buildChildItemPath(t.index);
        return acc;
      }, {});
    }

    if ((changes['isActive'] && this.isActive) || (changes['isHoverTarget'] && this.isHoverTarget)) {
      requestAnimationFrame(() => this.scrollIntoViewIfNeeded());
    }
  }

  ngOnDestroy() {
    this.logger.log('ngOnDestroy');
    ComponentInstanceTracker.decrement('SentenceComponent');
  }

  buildChildItemPath(index: number): DisclosurePath {
    return {
      ...this.thisItemPath,
      wordIndex: index,
    };
  }

  // return true if component should be displayed
  get showComponent(): boolean {
    // TODO: this should be based on the sentence state (editState and spellingState .. if 'dirty')
    return true;
  }

  onOpened() {
    this.disclosurePathService.setCurrent(this.thisItemPath);
  }

  onClosed() {
    // Only clear if this sentence is still the current path (guard against
    // stale close events when another panel has already taken over).
    // NOTE: clearCurrent() must be used here — setCurrent({...null}) is a no-op
    // because isCurrent() treats null fields as wildcards and always returns
    // "no change", so the BehaviorSubject would never emit.
    if (this.disclosurePathService.isCurrent(this.thisItemPath)) {
      this.disclosurePathService.clearCurrent();
    }
  }

  /** Called by mat-expansion-panel (afterExpand) — bring the expanded content into view. */
  scrollToExpanded(): void {
    const el = this.sentenceRoot?.nativeElement;
    const container = el ? this.findScrollContainer(el) : null;
    if (!el || !container) return;

    // rAF ensures the animation has fully painted before we measure.
    requestAnimationFrame(() => this.scrollToShowBottom(el, container));
  }

  onDoubleClickPanel(event: Event) {
    event.stopPropagation();
    this.logger.log('onDoubleClickPanel:', this.thisItemPath, this.sentence);
    this.onMakeReplacement(this.thisItemPath);
  }

  onMakeReplacement(path: DisclosurePath): void {
    this.logger.log('onMakeReplacement:', this.thisItemPath, this.sentence);
    if (path.paragraphIndex == null || path.sentenceIndex == null) {
      this.logger.warn('onMakeReplacement: incomplete path', path);
      return;
    }
    this.replacementService.makeSentenceReplacement(this.elId, path.paragraphIndex, path.sentenceIndex);
  }

  onSidebarMouseEnter(): void {
    this.isDirectHover = true;
    this.disclosurePathService.setHoveredPath(this.thisItemPath);
  }

  onSidebarMouseLeave(): void {
    this.isDirectHover = false;
    this.disclosurePathService.clearHoveredPath();
  }

  // ── Scroll helpers ────────────────────────────────────────────────────────

  /**
   * Scroll this sentence into view when driven by cursor or hover position.
   * - Already fully visible → no scroll (no noise).
   * - Taller than the container → pin header at container top.
   * - Otherwise → center in container.
   */
  private scrollIntoViewIfNeeded(): void {
    const el = this.sentenceRoot?.nativeElement;
    const container = el ? this.findScrollContainer(el) : null;
    if (!el || !container) {
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const elRect = el.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();

    const topHidden   = elRect.top < cRect.top;
    const bottomHidden = elRect.bottom > cRect.bottom;

    if (!topHidden && !bottomHidden) return; // fully visible — do nothing

    // If the element is taller than the container, centering would hide the header.
    // Instead pin the header to the container top (same rule as scrollToExpanded).
    if (el.clientHeight >= container.clientHeight) {
      this.scrollToShowHeader(el, container);
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Scroll minimally to reveal the bottom of the element.
   * Never scrolls far enough to push the element's own top off-screen.
   * If the element is taller than the container, pin the header at the top edge.
   */
  private scrollToShowBottom(el: HTMLElement, container: HTMLElement): void {
    const elRect = el.getBoundingClientRect();
    const cRect  = container.getBoundingClientRect();

    if (elRect.bottom <= cRect.bottom) return; // already in view

    const scrollNeeded             = elRect.bottom - cRect.bottom;
    const maxBeforeHeaderHides     = Math.max(0, elRect.top - cRect.top);
    const scrollAmount             = Math.min(scrollNeeded, maxBeforeHeaderHides);

    if (scrollAmount > 0) {
      container.scrollBy({ top: scrollAmount, behavior: 'smooth' });
    }
    // If scrollAmount === 0: element taller than container — header is at top
    // edge already; leave it there.
  }

  /**
   * Scroll so the element's header (top) is at the container's top edge.
   * Used when the element is too tall to center without hiding the header.
   */
  private scrollToShowHeader(el: HTMLElement, container: HTMLElement): void {
    const elRect = el.getBoundingClientRect();
    const cRect  = container.getBoundingClientRect();
    const delta  = elRect.top - cRect.top;
    if (delta !== 0) {
      container.scrollBy({ top: delta, behavior: 'smooth' });
    }
  }

  /** Walk up the DOM to find the nearest overflow:auto/scroll ancestor. */
  private findScrollContainer(el: HTMLElement): HTMLElement | null {
    let node: HTMLElement | null = el.parentElement;
    while (node && node !== document.documentElement) {
      const overflow = getComputedStyle(node).overflowY;
      if (overflow === 'auto' || overflow === 'scroll') return node;
      node = node.parentElement;
    }
    return null;
  }

  trackByFn(index: number, item: tokenDTO): number {
    return item.index;
  }
}
